import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { DotNavigator } from './dot-navigator'
import { QuestionCard } from './question-card'

interface AnswerOption {
    value: number
    label: string
    description?: string
}

interface Question {
    id: string
    domain_id: string
    domain_name?: string
    category: string
    text: string
    description?: string
    maturity_level?: number
    answer_options?: AnswerOption[]
}

interface QuestionCarouselProps {
    questions: Question[]
    answers: Record<string, number>
    comments: Record<string, string>
    attachments: Record<string, File | null>
    existingAttachments: Record<string, string>
    onAnswerChange: (questionId: string, value: number) => void
    onCommentChange: (questionId: string, comment: string) => void
    onFileChange: (questionId: string, file: File | null) => void
    onDownloadFile: (questionId: string) => void
    readOnly: boolean
    isSaving: boolean
    autoAdvance?: boolean
    autoAdvanceDelay?: number
    startAtFirstUnanswered?: boolean
    /** Called when carousel reaches end and user tries to go next */
    onNextDomain?: () => void
    assessmentId: string
}

const slideVariants = {
    enter: (direction: number) => ({
        x: direction > 0 ? 200 : -200,
        opacity: 0,
    }),
    center: {
        x: 0,
        opacity: 1,
    },
    exit: (direction: number) => ({
        x: direction > 0 ? -200 : 200,
        opacity: 0,
    }),
}

export function QuestionCarousel({
    questions,
    answers,
    comments,
    attachments,
    existingAttachments,
    onAnswerChange,
    onCommentChange,
    onFileChange,
    onDownloadFile,
    readOnly,
    isSaving,
    autoAdvance = true,
    autoAdvanceDelay = 600,
    startAtFirstUnanswered = false,
    onNextDomain,
    assessmentId,
}: QuestionCarouselProps) {
    // Compute initial index: first unanswered question, or 0
    const initialIndex = useMemo(() => {
        if (!startAtFirstUnanswered) return 0
        const idx = questions.findIndex(q => answers[q.id] === undefined)
        return idx >= 0 ? idx : 0
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [activeIndex, setActiveIndex] = useState(initialIndex)
    const [direction, setDirection] = useState(0)

    const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current)
        }
    }, [])

    const goTo = useCallback((index: number) => {
        if (index < 0 || index >= questions.length || index === activeIndex) return
        if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current)

        setDirection(index > activeIndex ? 1 : -1)
        setActiveIndex(index)
    }, [activeIndex, questions.length])

    const goNext = useCallback(() => {
        if (activeIndex >= questions.length - 1) {
            onNextDomain?.()
        } else {
            goTo(activeIndex + 1)
        }
    }, [activeIndex, goTo, questions.length, onNextDomain])
    const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo])

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return
            if (e.key === 'ArrowRight') goNext()
            if (e.key === 'ArrowLeft') goPrev()
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [goNext, goPrev])

    const handleAnswerChange = useCallback((questionId: string, value: number) => {
        onAnswerChange(questionId, value)

        if (autoAdvance && !readOnly) {
            if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current)
            autoAdvanceTimer.current = setTimeout(() => {
                if (activeIndex < questions.length - 1) {
                    goTo(activeIndex + 1)
                } else {
                    onNextDomain?.()
                }
            }, autoAdvanceDelay)
        }
    }, [onAnswerChange, autoAdvance, activeIndex, questions.length, readOnly, autoAdvanceDelay, goTo, onNextDomain])

    const currentQuestion = questions[activeIndex]
    if (!currentQuestion) return null

    return (
        <div className="space-y-3">
            {/* Main content: Question | Dots */}
            <div className="flex gap-4">
                {/* Question area — 70% width */}
                <div className="w-[70%] min-w-0">
                    <div className="relative overflow-hidden min-h-[260px]">
                        <AnimatePresence mode="wait" custom={direction}>
                            <motion.div
                                key={currentQuestion.id}
                                custom={direction}
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{
                                    x: { type: 'spring', stiffness: 350, damping: 30 },
                                    opacity: { duration: 0.15 },
                                }}
                            >
                                <QuestionCard
                                    question={currentQuestion}
                                    questionNumber={activeIndex + 1}
                                    totalQuestions={questions.length}
                                    answer={answers[currentQuestion.id]}
                                    comment={comments[currentQuestion.id]}
                                    existingAttachment={existingAttachments[currentQuestion.id]}
                                    attachment={attachments[currentQuestion.id]}
                                    onAnswerChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                                    onCommentChange={(comment) => onCommentChange(currentQuestion.id, comment)}
                                    onFileChange={(file) => onFileChange(currentQuestion.id, file)}
                                    onDownloadFile={() => onDownloadFile(currentQuestion.id)}
                                    readOnly={readOnly}
                                    assessmentId={assessmentId}
                                />
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

                {/* Dot navigator sidebar — 30% width */}
                <div
                    className="w-[30%] border-l pl-3 pt-1 self-stretch overflow-y-auto"
                >
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                        Questions
                    </div>
                    <DotNavigator
                        questions={questions}
                        answers={answers}
                        attachments={attachments}
                        existingAttachments={existingAttachments}
                        activeIndex={activeIndex}
                        onDotClick={goTo}
                    />
                </div>
            </div>

            {/* Navigation Buttons — compact */}
            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={goPrev}
                    disabled={activeIndex === 0}
                    className="flex items-center gap-1 h-8 px-3 text-xs"
                >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Prev
                </Button>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {isSaving && (
                        <span className="flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Saving…
                        </span>
                    )}
                    <span className="font-medium">
                        {activeIndex + 1} / {questions.length}
                    </span>
                </div>

                <Button
                    variant="outline"
                    size="sm"
                    onClick={goNext}
                    disabled={activeIndex === questions.length - 1}
                    className="flex items-center gap-1 h-8 px-3 text-xs"
                >
                    Next
                    <ChevronRight className="h-3.5 w-3.5" />
                </Button>
            </div>
        </div>
    )
}
